/**
 * Frontend logging utility
 */

export enum LogLevel {
  DEBUG = 'DEBUG',
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR'
}

interface LogContext {
  userId?: string;
  analysisId?: string;
  component?: string;
  [key: string]: any;
}

class FrontendLogger {
  private context: LogContext = {};
  private logLevel: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.logLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || LogLevel.INFO;
    this.isDevelopment = import.meta.env.DEV || false;
  }

  setContext(context: LogContext): void {
    this.context = { ...this.context, ...context };
  }

  clearContext(): void {
    this.context = {};
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    return levels.indexOf(level) >= levels.indexOf(this.logLevel);
  }

  private formatMessage(level: LogLevel, message: string, data?: any): any[] {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      context: this.context,
      ...(data && { data })
    };

    if (this.isDevelopment) {
      // In development, return formatted output for better readability
      return [
        `[${timestamp}] ${level}: ${message}`,
        ...(Object.keys(this.context).length > 0 ? ['\nContext:', this.context] : []),
        ...(data ? ['\nData:', data] : [])
      ];
    } else {
      // In production, return structured JSON
      return [JSON.stringify(logEntry)];
    }
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(...this.formatMessage(LogLevel.DEBUG, message, data));
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(...this.formatMessage(LogLevel.INFO, message, data));
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(...this.formatMessage(LogLevel.WARN, message, data));
    }
  }

  error(message: string, error?: Error | any, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const errorData = error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error;
      
      console.error(...this.formatMessage(LogLevel.ERROR, message, {
        error: errorData,
        ...data
      }));
    }
  }

  // Convenience methods for common frontend operations
  logApiRequest(method: string, url: string, data?: any): void {
    this.debug(`API Request: ${method} ${url}`, data);
  }

  logApiResponse(method: string, url: string, status: number, data?: any): void {
    if (status >= 400) {
      this.error(`API Error: ${method} ${url}`, null, { status, data });
    } else {
      this.debug(`API Response: ${method} ${url}`, { status, data });
    }
  }

  logComponentMount(componentName: string): void {
    this.debug(`Component mounted: ${componentName}`);
  }

  logComponentUnmount(componentName: string): void {
    this.debug(`Component unmounted: ${componentName}`);
  }

  logUserAction(action: string, details?: any): void {
    this.info(`User action: ${action}`, details);
  }

  logStateChange(stateName: string, oldValue: any, newValue: any): void {
    this.debug(`State change: ${stateName}`, { oldValue, newValue });
  }
}

// Export singleton instance
export const logger = new FrontendLogger();

// Export convenience function for creating scoped loggers
export function createLogger(context: LogContext): FrontendLogger {
  const scopedLogger = new FrontendLogger();
  scopedLogger.setContext(context);
  return scopedLogger;
}
